'use client';

import { useStatsigClient } from '@statsig/react-bindings';
import { maxBy } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import {
  StatsigUserSyncStatus,
  useStatsigUserSyncStore,
} from '../../store/statsigUserSyncStore';
import { useTokenStore } from '../../store/tokenStore';
import { isStageDev } from '../../utils/common';
import { useMySubscriptionsQuery } from '../../utils/hook/react-query/useMySubscriptionsQuery';
import useMeQuery from '../../utils/hook/react-query/user/useMeQuery';
import useUserSegmentQuery from '../../utils/hook/react-query/user/useUserSegmentQuery';
import { isReactNative } from '../../utils/react-native';

/**
 * StatsigServerSideProvider가 초기화한 클라이언트를 대상으로
 * 로그인한 사용자 정보를 Statsig에 동기화하는 컴포넌트
 * - token 유무와 me 로드 상태에 따라 statsigUserSyncStore를 전이
 * - updateUserAsync를 await하여 완료 시점에 'synced'로 전환 (exposure 기록 준비 완료)
 * - 세대 카운터로 동시성 방어: 이전 Promise가 후행 상태를 덮어쓰지 않도록 함
 *
 * @link https://docs.statsig.com/client/javascript-sdk/next-js
 */

interface UserProperties {
  userID: string;
  custom: {
    /** 유지해야하는 프로퍼티 */
    subscription_count: number; // mixpanel과 동일: 구독 회차 중 가장 큰 값
    isApp: boolean;
    isSegmentTarget: boolean;

    /** 임시 프로퍼티 */
    newSubscriber: boolean; // 정기구독 미사용 유저 여부
  };
}

const StatsigUserSync = () => {
  const token = useTokenStore.use.token();
  const { me, isPending: isPendingMe } = useMeQuery();
  const { data: userSegment } =
    // 세그먼트 조회 필요시 다시 활성화 필요
    useUserSegmentQuery({ enabled: false });
  const { allActiveOrders, isPending: isPendingSubscription } =
    useMySubscriptionsQuery();

  const { client } = useStatsigClient();
  const setStatus = useStatsigUserSyncStore.use.setStatus();

  const hasSubscriptionHistory = !!me?.hasSubscriptionHistory;

  /** isSegmentTarget: 세그먼트 실험 타겟팅
   * - 세그먼트 대상 (hasSegment)
   * - 2회차 이상 구독한 유저 여부 (1회차 커마 제외, hasSecondSequenceOrAbove )
   * @link https://uglyushq.slack.com/archives/C03JX1SRCHX/p1760339926788109
   */
  const hasSecondSequenceOrAbove = allActiveOrders.some(
    (order) => order.sequence > 1,
  );

  const isSegmentTarget = !!userSegment?.hasSegment && hasSecondSequenceOrAbove;

  const userProperties = useMemo<UserProperties | null>(() => {
    if (!me) return null;
    return {
      userID: me.id.toString(),
      custom: {
        isSegmentTarget,
        newSubscriber: !hasSubscriptionHistory,
        isApp: isReactNative(),
        subscription_count: maxBy(allActiveOrders, 'sequence')?.sequence ?? 0,
      },
    };
  }, [me, isSegmentTarget, hasSubscriptionHistory, allActiveOrders]);

  // 최신 updateUserAsync 호출만 status 전이에 반영하기 위한 세대 카운터
  const syncRequestIdRef = useRef(0);
  useEffect(() => {
    // 미로그인 확정: token 부재
    if (!token) {
      setStatus(StatsigUserSyncStatus.NO_USER);
      return;
    }

    // 로그인 경로 데이터 로딩 중
    if (isPendingMe || isPendingSubscription) {
      setStatus(StatsigUserSyncStatus.PENDING);
      return;
    }

    // token은 있으나 me fetch 실패(401 등으로 토큰 무효화 중): 미로그인으로 간주
    if (!me || !userProperties) {
      setStatus(StatsigUserSyncStatus.NO_USER);
      return;
    }

    const requestId = ++syncRequestIdRef.current;
    setStatus(StatsigUserSyncStatus.PENDING);

    client
      .updateUserAsync(userProperties)
      .then(() => {
        if (requestId !== syncRequestIdRef.current) return;
        setStatus(StatsigUserSyncStatus.SYNCED);

        if (isStageDev) {
          console.log('[StatsigUserSync] User Properties 업데이트:', {
            userID: userProperties.userID,
            custom: userProperties.custom,
          });
        }
      })
      .catch((error) => {
        console.error('StatsigUserSync updateUserAsync error', error);
        if (requestId !== syncRequestIdRef.current) return;
        // 네트워크 실패 시 익명 userID로 exposure가 기록되지 않도록 NO_USER로 fallback
        // - authenticatedOnly=true 실험: control(0) + exposure 미기록으로 데이터 오염 방지
        // - authenticatedOnly=false 실험: 자동 생성 stableID로 정상 평가 가능
        setStatus(StatsigUserSyncStatus.NO_USER);
      });
  }, [
    token,
    me,
    isPendingMe,
    isPendingSubscription,
    userProperties,
    client,
    setStatus,
  ]);

  return null;
};

export default StatsigUserSync;
